<script setup>
import { defineProps, ref, computed } from 'vue'
import { provideDocExpansion } from '@/composables/useDocExpansion'
import DocSection from '@/components/Docs/Section.vue'
import JsonBlock from '@/components/Docs/JsonBlock.vue'
import StringParam from '@/components/Docs/Parameters/StringParam.vue'
import MultiSelectParam from '@/components/Docs/Parameters/MultiSelectParam.vue'
import EndpointBlock from '@/components/Docs/EndpointBlock.vue'
import BasicAttribute from '@/components/Docs/Attributes/BasicAttribute.vue'

defineProps({ anchor: String })

let request_query = ref("*")
let request_field = ref("id.resp_h")
let request_terms_size = ref("10")
let request_aggregations = ref(["terms"])
let request_log_types = ref([])

const aggregationOptions = [
  { value: 'terms', label: 'Terms' },
  { value: 'cardinality', label: 'Cardinality' },
  { value: 'multi_cardinality', label: 'Multi cardinality' },
  { value: 'significant_terms', label: 'Significant terms' },
  { value: 'composite', label: 'Composite' },
  { value: 'hourly', label: 'Hourly' },
]

const logTypeOptions = [
  { value: 'conn', label: 'Connection Logs' },
  { value: 'dns', label: 'DNS Queries' },
  { value: 'http', label: 'HTTP Traffic' },
  { value: 'ssl', label: 'SSL/TLS' },
  { value: 'weird', label: 'Anomalies' },
  { value: 'x509', label: 'Certificates' },
  { value: 'files', label: 'File Transfers' },
  { value: 'notice', label: 'Notices' },
]

let request_body = computed(() => {
  return {
    query: request_query.value,
    field: request_field.value,
    terms_size: parseInt(request_terms_size.value) || 10,
    aggregations: request_aggregations.value,
    log_types: request_log_types.value,
  }
})

const { triggerExpandAll } = provideDocExpansion()
const allExpanded = ref(false)
const toggleAllExpansion = () => {
  allExpanded.value = !allExpanded.value
  triggerExpandAll(allExpanded.value)
}
</script>

<template>
  <doc-section title="Aggregate" :anchor="anchor">
    <template v-slot:left>
      <div class="mb-5">
        <p>
          Compute aggregations over ingested event logs, top values, unique counts,
          significant terms, composites and hourly histograms, without pulling the
          underlying documents. Combine with a query and log-type / source filters
          to slice your data.
        </p>
        <p>
          Sources and log types are scoped to your organization's grants.
        </p>
      </div>

      <div class="mb-5">
        <h4 class="mb-4">Request parameters</h4>

        <StringParam class="mb-3" name="Query" :required="true"
          description="Query string constraining the documents to aggregate over (e.g. '*', 'proto:tcp')"
          type="string" :value="request_query" @value="(v) => request_query = v" />

        <MultiSelectParam class="mb-3" name="Aggregations" :required="true"
          description="One or more aggregation kinds to run"
          :options="aggregationOptions" :value="request_aggregations" @value="(v) => request_aggregations = v" />

        <StringParam class="mb-3" name="Field" :required="false"
          description="Field to aggregate on (required for terms / cardinality style aggregations)"
          type="string" :value="request_field" @value="(v) => request_field = v" />

        <StringParam class="mb-3" name="Terms size" :required="false"
          description="Number of buckets to return for terms aggregations"
          type="integer" :value="request_terms_size" @value="(v) => request_terms_size = v" />

        <MultiSelectParam class="mb-3" name="Log Types" :required="false"
          description="Filter to specific log types (leave empty for all accessible types)"
          :options="logTypeOptions" :value="request_log_types" @value="(v) => request_log_types = v" />
      </div>

      <div>
        <div class="d-flex justify-content-between align-items-center mb-4">
          <h4 class="mb-0">Response attributes</h4>
          <button @click="toggleAllExpansion" class="btn-expand-collapse">
            <font-awesome-icon :icon="['fas', allExpanded ? 'compress-alt' : 'expand-alt']" />
            {{ allExpanded ? 'Collapse All' : 'Expand All' }}
          </button>
        </div>

        <BasicAttribute name="status" type="string" description="Response status (success or error)" />

        <BasicAttribute name="data" type="object" description="Aggregation results. The exact shape depends on the requested aggregation kinds." :depth="1">
          <BasicAttribute name="buckets" type="array" description="Result buckets for terms / composite / significant-terms aggregations" :depth="2">
            <BasicAttribute name="key" type="string" description="The bucket value" />
            <BasicAttribute name="doc_count" type="integer" description="Number of matching documents in this bucket" />
          </BasicAttribute>
          <BasicAttribute name="value" type="integer" description="Scalar result for cardinality-style aggregations" :depth="2" />
        </BasicAttribute>
      </div>
    </template>

    <template v-slot:right>
      <endpoint-block
        title="/query/event/aggregate"
        method="POST"
        name="Event Aggregate"
        query="/query/event/aggregate"
        :body="request_body"
      />

      <json-block title="Response" class="mt-3">
{
  "status": "success",
  "data": {
    "buckets": [
      { "key": "8.8.8.8", "doc_count": 128 },
      { "key": "1.1.1.1", "doc_count": 74 }
    ]
  }
}
      </json-block>
    </template>
  </doc-section>
</template>

<style scoped>
.btn-expand-collapse {
  background-color: transparent;
  border: 1px solid var(--border-subtle);
  color: var(--text-faint);
  padding: 0.5rem 1rem;
  border-radius: var(--radius-md);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-expand-collapse:hover {
  background-color: var(--surface-overlay-2);
  color: var(--text-primary);
}
</style>
